import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useMembers, useInviteMember, useUpdateMember, useArchiveMember } from '@/hooks/use-members';
import { relativeTime } from '@/lib/format';

export const Route = createFileRoute('/_authenticated/admin/members')({
  component: MembersPage,
});

function MembersPage() {
  const { data, isLoading } = useMembers();
  const invite = useInviteMember();
  const update = useUpdateMember();
  const archive = useArchiveMember();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'viewer'>('viewer');
  const [open, setOpen] = useState(false);

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 animate-fade-up">
      <header className="mb-6 flex items-start justify-between gap-6">
        <div>
          <h1 className="mb-1 text-2xl font-semibold tracking-tight">Members</h1>
          <p className="text-sm text-text-muted">{data?.members.length ?? 0} active members</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="size-3.5" /> Invite member
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Invite a member</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                invite.mutate(
                  { email, role },
                  {
                    onSuccess: () => {
                      toast.success(`Invited ${email}`);
                      setEmail('');
                      setOpen(false);
                    },
                    onError: () => toast.error('Failed to invite'),
                  },
                );
              }}
              className="space-y-4 py-2"
            >
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@alivevietnam.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={role} onValueChange={(v) => setRole(v as 'admin' | 'viewer')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={invite.isPending}>{invite.isPending ? 'Inviting…' : 'Invite'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="rounded-lg border border-border bg-bg-surface divide-y divide-border">
          {data?.members.map((m) => (
            <div key={m.id} className="flex items-center gap-4 p-3">
              <div className="flex size-8 items-center justify-center rounded-full bg-gradient-to-br from-accent to-[#5AC8FA] text-[11px] font-semibold text-white">
                {m.email.slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{m.name || m.email.split('@')[0]}</div>
                <div className="text-xs text-text-muted font-mono">{m.email}</div>
              </div>
              <div className="text-xs text-text-muted">
                {m.last_active ? `Active ${relativeTime(m.last_active * 1000)}` : 'Never logged in'}
              </div>
              <Select value={m.role} onValueChange={(v) => update.mutate({ id: m.id, role: v as 'admin' | 'viewer' })}>
                <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Viewer</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (confirm(`Remove ${m.email}?`)) {
                    archive.mutate(m.id, { onSuccess: () => toast.success(`${m.email} removed`) });
                  }
                }}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
